import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Undo, Camera, RefreshCw, AlertCircle, Loader2 } from 'lucide-react';
import Webcam from 'react-webcam';

interface WebcamCaptureProps {
  onCapture: (imageData: string) => void;
  onBack: () => void;
}

export interface WebcamCaptureRef {
  takePhoto: () => void;
}

const WebcamCapture = forwardRef<WebcamCaptureRef, WebcamCaptureProps>(({ 
  onCapture,
  onBack
}, ref) => {
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentSubtextIndex, setCurrentSubtextIndex] = useState(0);
  
  const webcamRef = useRef<Webcam>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const subtextIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Same subtexts as step 3 in ProcessingPopup
  const AI_GENERATION_SUBTEXTS = [
    'Creating AI illustration template',
    'Gathering my brushes',
    'Mixing my oil paints',
    'Sharpening my pencils',
    'Creating watercolor illustration',
    'Creating oil painting',
    'Creating pencil drawing'
  ];

  // Set initializing to false after mounting
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitializing(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  // Handle subtext cycling when processing
  useEffect(() => {
    if (isProcessing) {
      // Start cycling through subtexts
      subtextIntervalRef.current = setInterval(() => {
        setCurrentSubtextIndex(prevIndex => {
          // After message 6, go back to message 4 (like in ProcessingPopup)
          if (prevIndex < 6) {
            return prevIndex + 1;
          } else {
            return 4;
          }
        });
      }, 3000); // 3 second intervals
    } else {
      // Clear interval when not processing
      if (subtextIntervalRef.current) {
        clearInterval(subtextIntervalRef.current);
      }
      setCurrentSubtextIndex(0);
    }

    // Cleanup function
    return () => {
      if (subtextIntervalRef.current) {
        clearInterval(subtextIntervalRef.current);
      }
    };
  }, [isProcessing]);

  // Switch between front and rear cameras
  const switchCamera = () => {
    setFacingMode(current => current === 'environment' ? 'user' : 'environment');
  };

  // Take photo
  const takePhoto = () => {
    try {
      setError(null);
      
      if (webcamRef.current) {
        const imageSrc = webcamRef.current.getScreenshot();
        if (!imageSrc) {
          throw new Error('Failed to capture image');
        }
        
        setCapturedImage(imageSrc);
        setIsProcessing(true);
        
        // Delay the callback slightly to allow the UI to update
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        
        timeoutRef.current = setTimeout(() => {
          onCapture(imageSrc);
        }, 300);
      } else {
        throw new Error('Camera not initialized');
      }
    } catch (err) {
      console.error('Error taking photo:', err);
      setError(err instanceof Error ? err.message : 'Failed to capture image');
    }
  };

  // Retry camera initialization
  const retryCamera = () => {
    setError(null);
    setIsInitializing(true);
    
    // Short delay to re-initialize
    setTimeout(() => {
      setIsInitializing(false);
    }, 1000);
  };

  // Expose takePhoto method to parent via ref
  useImperativeHandle(ref, () => ({
    takePhoto
  }));

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (subtextIntervalRef.current) {
        clearInterval(subtextIntervalRef.current);
      }
    };
  }, []);

  const videoConstraints = {
    width: { ideal: 1280 },
    height: { ideal: 720 },
    facingMode: facingMode,
  };

  if (error) {
    return (
      <div className="bg-red-50 p-6 rounded-lg text-center">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-3" />
        <p className="text-red-600 mb-4">{error}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button 
            onClick={retryCamera}
            className="px-4 py-2 bg-gray-700 text-white rounded-full hover:bg-gray-800 flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Try Again</span>
          </button>
          <button 
            onClick={onBack}
            className="px-4 py-2 bg-black text-white rounded-full hover:bg-gray-800 flex items-center justify-center gap-2"
          >
            <Undo className="w-4 h-4" />
            <span>Go Back</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {isInitializing ? (
        <div className="h-64 flex items-center justify-center bg-black">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </div>
      ) : (
        <div className="flex flex-col">
          {capturedImage ? (
            // Show captured image with darkened overlay and spinner only
            <div className="relative">
              <img 
                src={capturedImage} 
                alt="Captured" 
                className="w-full max-h-[320px] object-contain"
              />
              {isProcessing && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                  <div className="flex flex-col items-center justify-center text-center px-4">
                    <Loader2 className="w-10 h-10 text-white animate-spin mb-4" />
                    <div className="text-white text-sm font-medium">
                      {AI_GENERATION_SUBTEXTS[currentSubtextIndex]}
                    </div>
                  </div>
                </div>
              )}
              {!isProcessing && (
                <div className="absolute top-2 left-2">
                  <button 
                    onClick={() => setCapturedImage(null)}
                    className="bg-black bg-opacity-50 text-white p-2 rounded-full"
                    aria-label="Retake photo"
                  >
                    <Undo className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          ) : (
            // Show live camera feed
            <div className="relative bg-black">
              <Webcam
                ref={webcamRef}
                audio={false}
                height={720}
                width={1280}
                videoConstraints={videoConstraints}
                screenshotFormat="image/jpeg"
                screenshotQuality={0.95}
                className="w-full max-h-[320px] object-cover"
                onUserMediaError={(err) => {
                  console.error('Webcam error:', err);
                  setError('Could not access camera. Please ensure you have granted camera permissions and your device has a working camera.');
                }}
              />
              
              <div className="absolute top-2 left-2 flex gap-2">
                <button 
                  onClick={onBack}
                  className="bg-black bg-opacity-50 text-white p-2 rounded-full"
                  aria-label="Back"
                >
                  <Undo className="w-5 h-5" />
                </button>
                
                <button 
                  onClick={switchCamera}
                  className="bg-black bg-opacity-50 text-white p-2 rounded-full"
                  aria-label="Switch camera"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
          
          {/* Capture button below camera feed */}
          <div className="mt-4 flex justify-center">
            {!capturedImage ? (
              <button
                onClick={takePhoto}
                className="bg-black text-white py-3 px-6 rounded-full flex items-center justify-center gap-2 hover:bg-gray-800 transition-colors"
              >
                <Camera className="w-5 h-5" />
                <span>Capture</span>
              </button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
});

export default WebcamCapture;